import cn from 'classnames';
// React
import { forwardRef } from 'react';
// Types
import Slider from 'react-slick';
import { Settings } from 'react-slick';
import {
  GetAthleteStreams_liveAndUpcomingStreams_entities,
  GetAthleteStreams_pastStreams_entities,
} from 'api/streams/types/GetAthleteStreams';
// Hooks
import { useStreamNavigation } from 'hooks/useStreamNavigation';
import { useGetCurrUser, useGetStream } from 'hooks';
// Constants
import { ATHLETES } from 'constants/routes';
// Helpers
import { getStreamStatus } from 'helpers/streams';
// Ui
import ContentCard from 'ui3/Card/ContentCard';
import Carousel from 'ui3/Carousel/Carousel';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import styles from './RelatedStreams.module.scss';

const sliderSettings = {
  infinite: false,
  adaptiveHeight: false,
  speed: 500,
  swipeToSlide: true,
  variableWidth: true,
  slidesToScroll: 1,
  arrows: false,
  dots: false,
  centerPadding: '0px',
} as Settings;

type RelatedStreamsProps = {
  streams:
    | GetAthleteStreams_liveAndUpcomingStreams_entities[]
    | GetAthleteStreams_pastStreams_entities[];
  className?: string;
  ppvStreamId?: string;
  arrowsBelowOnMobile?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  showArrows?: boolean;
  padding?: 'default' | 'none';
  beforeChange?: (current: number, next: number) => void;
};

const RelatedStreams = forwardRef<
  InstanceType<typeof Slider>,
  RelatedStreamsProps
>(
  (
    {
      streams,
      className,
      ppvStreamId,
      arrowsBelowOnMobile = false,
      onPrev,
      onNext,
      showArrows = false,
      padding = 'default',
      beforeChange,
    },
    ref
  ) => {
    const { data: currentUser } = useGetCurrUser();
    const getStreamNavigationProps = useStreamNavigation(
      currentUser?.me.id || null
    );

    const { data: ppvStreamData } = useGetStream({
      variables: {
        input: {
          id: ppvStreamId,
        },
      },
      skip: !ppvStreamId,
    });

    if (!streams.length && !ppvStreamData) {
      return null;
    }

    const finalStreams =
      ppvStreamData?.stream &&
      !streams.find((stream) => stream.id === ppvStreamId)
        ? [ppvStreamData.stream, ...streams]
        : streams;

    const settings: Settings = beforeChange
      ? { ...sliderSettings, beforeChange }
      : sliderSettings;

    return (
      <div
        className={cn(styles.root, className, {
          [styles.noPadding]: padding === 'none',
        })}
      >
        <div className={styles.rootCarousel}>
          <Carousel ref={ref} {...settings} className={styles.slider}>
            {finalStreams.map((stream, index) => {
              const { linkUrl } = getStreamNavigationProps(stream);

              return (
                <ContentCard
                  key={stream.id}
                  type={stream.type}
                  status={getStreamStatus(
                    stream.repeatsEveryEnum,
                    stream.isHidden,
                    stream.streamStatus
                  )}
                  imageUrl={stream?.imageURL || ''}
                  linkUrl={linkUrl}
                  dateTime={stream?.scheduleDate || ''}
                  tzCode={stream?.timeZone?.tzCode || null}
                  title={stream?.name || ''}
                  // Auto-freed PPV keeps a non-zero `price`, so branch on `isFree`.
                  price={
                    stream?.isFree || stream?.price === 0
                      ? 'free'
                      : `$${stream?.price.toString()}`
                  }
                  user={stream.store?.storeDetails?.storeName || ''}
                  userUrl={
                    stream.store?.slug ? `/${stream.store?.slug}` : ATHLETES
                  }
                  isPlay
                  priorityImageLoading={index <= 1}
                />
              );
            })}
          </Carousel>
          {arrowsBelowOnMobile && showArrows && onPrev && onNext && (
            <div className={styles.mobileArrows}>
              <button
                className={styles.arrowButton}
                onClick={onPrev}
                aria-label="Previous"
              >
                <MillionsIcon name="arrowLeft" className={styles.arrowIcon} />
              </button>
              <button
                className={styles.arrowButton}
                onClick={onNext}
                aria-label="Next"
              >
                <MillionsIcon name="arrowRight" className={styles.arrowIcon} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
);

RelatedStreams.displayName = 'RelatedStreams';

export default RelatedStreams;
